import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Employee, ContractStatus } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from "@/hooks/ui/use-toast";
import { useRoles } from '@/hooks/employees/useRoles';
import { useSubscription } from '@/context/SubscriptionContext';
import { Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';

interface AddEmployeeFormProps {
  businessId: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onEmployeeAdded: (newEmployee: Employee) => void;
}

interface EmployeeFormRow {
  id: string;
  name: string;
  email: string;
  roleId: string | null;
  salaryAdjustment: number | null;
  contractStatus: ContractStatus;
}

const AddEmployeeForm: React.FC<AddEmployeeFormProps> = ({
  businessId,
  isOpen,
  onOpenChange,
  onEmployeeAdded,
}) => {
  const [employees, setEmployees] = useState<EmployeeFormRow[]>([
    { id: '1', name: '', email: '', roleId: null, salaryAdjustment: null, contractStatus: 'active' }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { checkLimit, subscriptionSummary } = useSubscription();

  // Fetch roles
  const { data: roles } = useRoles(businessId);

  const generateRandomPassword = () => {
    return Math.random().toString(36).slice(-10);
  };

  // Employee row management functions
  const addEmployee = () => {
    setEmployees([...employees, {
      id: Date.now().toString(),
      name: '',
      email: '',
      roleId: null,
      salaryAdjustment: null,
      contractStatus: 'active'
    }]);
  };

  const removeEmployee = (id: string) => {
    if (employees.length > 1) {
      setEmployees(employees.filter(emp => emp.id !== id));
    }
  };

  const updateEmployee = (id: string, field: keyof EmployeeFormRow, value: string | number | null) => {
    setEmployees(employees.map(emp =>
      emp.id === id ? { ...emp, [field]: value } : emp
    ));
  };

  const resetForm = () => {
    setEmployees([{ id: '1', name: '', email: '', roleId: null, salaryAdjustment: null, contractStatus: 'active' }]);
  };

  // Count valid employees for button text
  const validEmployees = employees.filter(emp => emp.name.trim() && emp.email.trim());
  const validCount = validEmployees.length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (validCount === 0) {
      toast.error('Please fill in at least one employee with name and email.');
      setIsSubmitting(false);
      return;
    }

    // Check subscription limits
    const canCreate = await checkLimit('employees', businessId, validCount);
    if (!canCreate) {
      const currentCount = subscriptionSummary?.employees_used || 0;
      const limit = subscriptionSummary?.employees_limit;
      const available = limit ? Math.max(0, limit - currentCount) : 0;

      toast.error('Employee Limit Exceeded', {
        description: limit
          ? validCount === 1
            ? `You've reached your limit of ${limit} employee${limit === 1 ? '' : 's'}. Upgrade your plan to add more.`
            : `You can only add ${available} more employee${available === 1 ? '' : 's'} (${currentCount}/${limit} used). Upgrade your plan.`
          : "Unable to add employees. Please check your subscription status."
      });
      setIsSubmitting(false);
      return;
    }

    const results: Employee[] = [];
    const errors: string[] = [];

    for (const employee of validEmployees) {
      try {
        let user_id = null;
        const generatedPassword = generateRandomPassword();

        // Create user via Edge Function
        const { data: userData, error: userError } = await supabase.functions.invoke('create-employee-user', {
          body: {
            email: employee.email,
            password: generatedPassword,
            email_confirm: true
          },
        });

        if (userError) {
          throw userError;
        }

        if (userData && !userData.success) {
          throw new Error(userData.error || 'Failed to create user');
        }

        if (userData?.user) {
          user_id = userData.user.id;
          // Send password reset email
          const { error: resetError } = await supabase.auth.resetPasswordForEmail(employee.email, {
            redirectTo: `${window.location.origin}/sign-in`,
          });
          if (resetError) {
            console.error(`Error sending password reset email to ${employee.email}:`, resetError);
          }
        }

        // Insert employee record
        const { data, error } = await supabase
          .from('employees')
          .insert([{
            business_id: businessId,
            name: employee.name.trim(),
            email: employee.email.trim(),
            role_id: employee.roleId,
            salary_adjustment: employee.salaryAdjustment,
            employment_status: employee.contractStatus,
            status_effective_date: new Date().toISOString().split('T')[0],
            user_id
          }])
          .select()
          .single();

        if (error) {
          throw error;
        }

        if (data) {
          results.push(data as Employee);
          onEmployeeAdded(data as Employee);
        }

      } catch (error: unknown) {
        console.error(`Error adding employee ${employee.name}:`, error);
        let errorMessage = error instanceof Error ? error.message : String(error);

        if (errorMessage?.includes('email address already exists') || errorMessage?.includes('already been registered')) {
          errorMessage = `Email ${employee.email} already exists`;
        }

        errors.push(`${employee.name}: ${errorMessage}`);
      }
    }

    // Show results
    if (results.length > 0) {
      toast.success(
        results.length === 1
          ? 'Employee added successfully!'
          : `Successfully added ${results.length} employees!`
      );
    }

    if (errors.length > 0) {
      toast.error(
        errors.length === 1
          ? 'Failed to add employee'
          : `Failed to add ${errors.length} employees`,
        {
          description: errors.slice(0, 3).join('; ') + (errors.length > 3 ? '...' : '')
        }
      );
    }

    // Close dialog if all succeeded
    if (errors.length === 0) {
      resetForm();
      onOpenChange(false);
    }

    setIsSubmitting(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { onOpenChange(open); if (!open) resetForm(); }}>
      <DialogContent className="sm:max-w-[900px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Employee{employees.length > 1 ? 's' : ''}</DialogTitle>
          <DialogDescription>
            Fill in the details below. A password reset email will be sent to each employee.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-3">
            {/* Header row */}
            <div className="grid grid-cols-12 gap-2 px-3 text-xs font-medium text-muted-foreground">
              <div className="col-span-3">Name *</div>
              <div className="col-span-3">Email *</div>
              <div className="col-span-2">Role</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Salary Adj.</div>
            </div>

            {/* Employee rows */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {employees.map((employee) => (
                <div key={employee.id} className="grid grid-cols-12 gap-2 p-2 border rounded-lg bg-muted/30">
                  <div className="col-span-3">
                    <Input
                      value={employee.name}
                      onChange={(e) => updateEmployee(employee.id, 'name', e.target.value)}
                      placeholder="John Doe"
                      className="h-9"
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      type="email"
                      value={employee.email}
                      onChange={(e) => updateEmployee(employee.id, 'email', e.target.value)}
                      placeholder="john@example.com"
                      className="h-9"
                    />
                  </div>
                  <div className="col-span-2">
                    <Select
                      value={employee.roleId || 'none'}
                      onValueChange={(value) => updateEmployee(employee.id, 'roleId', value === 'none' ? null : value)}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Assign later" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Assign later</SelectItem>
                        {roles?.map((role) => (
                          <SelectItem key={role.id} value={role.id}>
                            {role.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Select
                      value={employee.contractStatus}
                      onValueChange={(value) => updateEmployee(employee.id, 'contractStatus', value as ContractStatus)}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            Active
                          </div>
                        </SelectItem>
                        <SelectItem value="probation">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                            Probation
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 flex gap-1">
                    <Input
                      type="number"
                      step="0.01"
                      value={employee.salaryAdjustment ?? ''}
                      onChange={(e) => updateEmployee(employee.id, 'salaryAdjustment', e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="0.00"
                      className="h-9 flex-1"
                    />
                    <Button
                      type="button"
                      onClick={() => removeEmployee(employee.id)}
                      size="sm"
                      variant="ghost"
                      disabled={employees.length === 1}
                      className="h-9 w-9 p-0 shrink-0 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Add another employee button */}
            <div className="flex justify-start">
              <Button
                type="button"
                onClick={addEmployee}
                variant="outline"
                size="sm"
                className="border-dashed"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add another
              </Button>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={isSubmitting || validCount === 0}>
              {isSubmitting
                ? 'Adding...'
                : validCount <= 1
                  ? 'Add Employee'
                  : `Add ${validCount} Employees`
              }
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddEmployeeForm;
